import {
	COMPATIBILITY_WILDCARD,
	OfferActionField,
	OfferCompatibleWithField,
	OfferConditionField,
	OfferField,
	OfferGroupField,
	OfferMappingField,
	OfferRuleField,
	OfferStatus,
	OfferTermsBlockField,
	RuleOperator,
	TermsConditionType,
} from "../constants";
import { createEmptyCondition } from "./offerDraft";
import { isTermsBlockComplete } from "./offerDisplay";
import {
	buildMenuLabelsFromMapping,
	isApplyAll,
	normalizeMappingForDraft,
	normalizeMappingForPayload,
	normalizeMappingValue,
} from "./offerMapping";

/** Coerces a value to a finite number, or 0 when invalid. */
const toNumberOrZero = (value) => {
	const num = Number(value);
	return Number.isFinite(num) ? num : 0;
};

/** Parses an API date into a Date for the draft, or null when invalid. */
const toDraftDate = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

/** Keeps numbers for the draft and maps missing values to "". */
const toDraftNumberOrEmpty = (value) => (typeof value === "number" ? value : "");

/** Trims terms content for the create/update payload. */
const normalizeTermsBlock = (block) => {
	if (block[OfferTermsBlockField.TYPE] === TermsConditionType.ORDERED_LIST) {
		const content = Array.isArray(block[OfferTermsBlockField.CONTENT]) ? block[OfferTermsBlockField.CONTENT] : [];
		const normalizedContent = content.map((item) => String(item || "").trim()).filter(Boolean);
		return {
			[OfferTermsBlockField.TYPE]: block[OfferTermsBlockField.TYPE],
			[OfferTermsBlockField.CONTENT]: normalizedContent
		};
	}
	return {
		[OfferTermsBlockField.TYPE]: block[OfferTermsBlockField.TYPE],
		[OfferTermsBlockField.CONTENT]: String(block[OfferTermsBlockField.CONTENT] || "").trim(),
	};
};

/** Converts a compatible-with id list into the API's { [id]: true } map. */
const toCompatibilityMap = (ids) => {
	if (isApplyAll(ids)) {
		return { [COMPATIBILITY_WILDCARD]: true };
	}
	const map = {};
	(Array.isArray(ids) ? ids : []).forEach((id) => {
		if (id) map[id] = true;
	});
	return map;
};

/** Maps a form draft into the create/update offer API payload. */
export const mapOfferDraftToPayload = (draft, status = OfferStatus.DRAFT) => {
	const rule = draft[OfferField.RULE] || {};
	const action = rule[OfferRuleField.ACTION] || {};
	const mapping = draft[OfferField.OFFER_MAPPING] || {};

	return {
		[OfferField.DESCRIPTION]: draft[OfferField.DESCRIPTION] || "",
		[OfferField.START_DATE]: draft[OfferField.START_DATE]
			? new Date(draft[OfferField.START_DATE]).toISOString()
			: null,
		[OfferField.END_DATE]: draft[OfferField.END_DATE]
			? new Date(draft[OfferField.END_DATE]).toISOString()
			: null,
		[OfferField.MAX_USAGE_COUNT]: toNumberOrZero(draft[OfferField.MAX_USAGE_COUNT]),
		[OfferField.USAGE_COUNT]: toNumberOrZero(draft[OfferField.USAGE_COUNT]),
		[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]: toNumberOrZero(
			draft[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]
		),
		[OfferField.OFFER_TYPE]: draft[OfferField.OFFER_TYPE] || "",
		[OfferField.SUB_OFFER_TYPE]: draft[OfferField.SUB_OFFER_TYPE] || "",
		[OfferField.OFFER_CODE]: draft[OfferField.OFFER_CODE] || "",
		[OfferField.TITLE]: draft[OfferField.TITLE] || "",
		[OfferField.VERSION]: draft[OfferField.VERSION] || "1.0.0",
		[OfferField.PRIORITY]: toNumberOrZero(draft[OfferField.PRIORITY]),
		[OfferField.CHANNEL]: draft[OfferField.CHANNEL] || "",
		[OfferField.OFFER_GROUP_ID]: draft[OfferField.OFFER_GROUP_ID] || null,
		[OfferField.STATUS]: Number(status),
		[OfferField.TERMS_CONDITION]: (draft[OfferField.TERMS_CONDITION] || [])
			.filter(isTermsBlockComplete)
			.map(normalizeTermsBlock),
		[OfferField.RULE]: {
			[OfferRuleField.DESCRIPTION]: rule[OfferRuleField.DESCRIPTION] || "",
			[OfferRuleField.OPERATOR]: rule[OfferRuleField.OPERATOR] || RuleOperator.AND,
			[OfferRuleField.CONDITIONS]: (rule[OfferRuleField.CONDITIONS] || [])
				.filter(
					(c) =>
						c[OfferConditionField.SUBJECT] &&
						c[OfferConditionField.OPERATOR] &&
						c[OfferConditionField.VALUE] !== ""
				)
				.map((c) => ({
					[OfferConditionField.SUBJECT]: c[OfferConditionField.SUBJECT],
					[OfferConditionField.OPERATOR]: c[OfferConditionField.OPERATOR],
					[OfferConditionField.VALUE]: c[OfferConditionField.VALUE],
				})),
			[OfferRuleField.ACTION]: {
				[OfferActionField.TYPE]: action[OfferActionField.TYPE] || "",
				[OfferActionField.VALUE]: toNumberOrZero(action[OfferActionField.VALUE]),
				[OfferActionField.TARGET]: action[OfferActionField.TARGET] || "",
				[OfferActionField.MAX_AMOUNT_LIMIT]: toNumberOrZero(
					action[OfferActionField.MAX_AMOUNT_LIMIT]
				),
				[OfferActionField.MIN_AMOUNT_LIMIT]: toNumberOrZero(
					action[OfferActionField.MIN_AMOUNT_LIMIT]
				),
			},
		},
		[OfferField.OFFER_MAPPING]: {
			[OfferMappingField.MENU_IDS]: normalizeMappingForPayload(
				mapping[OfferMappingField.MENU_IDS]
			),
			[OfferMappingField.RESTAURANT_IDS]: normalizeMappingForPayload(
				mapping[OfferMappingField.RESTAURANT_IDS]
			),
			[OfferMappingField.USER_IDS]: normalizeMappingForPayload(
				mapping[OfferMappingField.USER_IDS]
			),
		},
		[OfferField.DOCUMENT_VERSION]: draft[OfferField.DOCUMENT_VERSION] || 0,
	};
};

/** Maps an offer-group form draft into the create group API payload. */
export const mapOfferGroupDraftToPayload = (draft) => {
	const compatibleWith = draft[OfferGroupField.COMPATIBLE_WITH] || {};
	return {
		[OfferGroupField.NAME]: draft[OfferGroupField.NAME] || "",
		[OfferGroupField.CAN_APPLY_MULTIPLE_OFFERS]:
			!!draft[OfferGroupField.CAN_APPLY_MULTIPLE_OFFERS],
		[OfferGroupField.COMPATIBLE_WITH]: {
			[OfferCompatibleWithField.INCLUDES]: toCompatibilityMap(
				compatibleWith[OfferCompatibleWithField.INCLUDES]
			),
			[OfferCompatibleWithField.EXCLUDES]: toCompatibilityMap(
				compatibleWith[OfferCompatibleWithField.EXCLUDES]
			),
		},
	};
};

/**
 * Maps a raw GET /offers/:id API response into an editable form draft.
 * Does not use display placeholders ("-"); menuLabels filled from enriched menu names when present.
 */
export const mapOfferResponseToDraft = (apiOffer = {}) => {
	const rule = apiOffer[OfferField.RULE] || {};
	const action = rule[OfferRuleField.ACTION] || {};
	const mapping = apiOffer[OfferField.OFFER_MAPPING] || {};
	const conditions = Array.isArray(rule[OfferRuleField.CONDITIONS])
		? rule[OfferRuleField.CONDITIONS]
		: [];

	return {
		id: apiOffer._id || apiOffer.id || "",
		[OfferField.TITLE]: apiOffer[OfferField.TITLE] || "",
		[OfferField.OFFER_CODE]: apiOffer[OfferField.OFFER_CODE] || "",
		[OfferField.DESCRIPTION]: apiOffer[OfferField.DESCRIPTION] || "",
		[OfferField.OFFER_TYPE]: apiOffer[OfferField.OFFER_TYPE] || "",
		[OfferField.SUB_OFFER_TYPE]: apiOffer[OfferField.SUB_OFFER_TYPE] || "",
		[OfferField.OFFER_GROUP_ID]: apiOffer[OfferField.OFFER_GROUP_ID] || "",
		[OfferField.PRIORITY]: toDraftNumberOrEmpty(apiOffer[OfferField.PRIORITY]),
		[OfferField.CHANNEL]: apiOffer[OfferField.CHANNEL] || "",
		[OfferField.START_DATE]: toDraftDate(apiOffer[OfferField.START_DATE]),
		[OfferField.END_DATE]: toDraftDate(apiOffer[OfferField.END_DATE]),
		[OfferField.MAX_USAGE_COUNT]: toDraftNumberOrEmpty(apiOffer[OfferField.MAX_USAGE_COUNT]),
		[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]: toDraftNumberOrEmpty(
			apiOffer[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]
		),
		[OfferField.USAGE_COUNT]:
			typeof apiOffer[OfferField.USAGE_COUNT] === "number"
				? apiOffer[OfferField.USAGE_COUNT]
				: 0,
		[OfferField.VERSION]: apiOffer[OfferField.VERSION] || "1.0.0",
		[OfferField.STATUS]: apiOffer[OfferField.STATUS],
		[OfferField.RULE]: {
			[OfferRuleField.DESCRIPTION]: rule[OfferRuleField.DESCRIPTION] || "",
			[OfferRuleField.OPERATOR]: rule[OfferRuleField.OPERATOR] || RuleOperator.AND,
			[OfferRuleField.CONDITIONS]:
				conditions.length > 0
					? conditions.map((c) => ({
							[OfferConditionField.SUBJECT]: c[OfferConditionField.SUBJECT] || "",
							[OfferConditionField.OPERATOR]: c[OfferConditionField.OPERATOR] || "",
							[OfferConditionField.VALUE]:
								c[OfferConditionField.VALUE] === 0 || c[OfferConditionField.VALUE]
									? c[OfferConditionField.VALUE]
									: "",
							[OfferConditionField.VALUE_LABEL]: c[OfferConditionField.VALUE_LABEL] || "",
						}))
					: [createEmptyCondition()],
			[OfferRuleField.ACTION]: {
				[OfferActionField.TYPE]: action[OfferActionField.TYPE] || "",
				[OfferActionField.VALUE]: toDraftNumberOrEmpty(action[OfferActionField.VALUE]),
				[OfferActionField.TARGET]: action[OfferActionField.TARGET] || "",
				[OfferActionField.MIN_AMOUNT_LIMIT]: toDraftNumberOrEmpty(
					action[OfferActionField.MIN_AMOUNT_LIMIT]
				),
				[OfferActionField.MAX_AMOUNT_LIMIT]: toDraftNumberOrEmpty(
					action[OfferActionField.MAX_AMOUNT_LIMIT]
				),
			},
		},
		[OfferField.OFFER_MAPPING]: {
			[OfferMappingField.MENU_IDS]: normalizeMappingForDraft(
				mapping[OfferMappingField.MENU_IDS]
			),
			[OfferMappingField.RESTAURANT_IDS]: normalizeMappingForDraft(
				mapping[OfferMappingField.RESTAURANT_IDS]
			),
			[OfferMappingField.USER_IDS]: normalizeMappingForDraft(
				mapping[OfferMappingField.USER_IDS]
			),
		},
		[OfferField.MENU_LABELS]: buildMenuLabelsFromMapping(mapping[OfferMappingField.MENU_IDS]),
		[OfferField.TERMS_CONDITION]: Array.isArray(apiOffer[OfferField.TERMS_CONDITION])
			? apiOffer[OfferField.TERMS_CONDITION]
			: [],
		[OfferField.DOCUMENT_VERSION]: apiOffer[OfferField.DOCUMENT_VERSION] || 0,
	};
};

/**
 * Maps a GET /offers/:id API response into a display-safe view model.
 * Keeps status as a string so OfferStatusBadge label lookup works.
 */
export const mapOfferResponseToView = (apiOffer = {}) => {
	const rule = apiOffer[OfferField.RULE] || {};
	const action = rule[OfferRuleField.ACTION] || {};
	const mapping = apiOffer[OfferField.OFFER_MAPPING] || {};
	const status = apiOffer[OfferField.STATUS];

	return {
		id: apiOffer._id || "",
		[OfferField.TITLE]: apiOffer[OfferField.TITLE] || "-",
		[OfferField.OFFER_CODE]: apiOffer[OfferField.OFFER_CODE] || "-",
		[OfferField.DESCRIPTION]: apiOffer[OfferField.DESCRIPTION] || "-",
		[OfferField.OFFER_TYPE]: apiOffer[OfferField.OFFER_TYPE] || "",
		[OfferField.SUB_OFFER_TYPE]: apiOffer[OfferField.SUB_OFFER_TYPE] || "-",
		[OfferField.OFFER_GROUP_ID]: apiOffer[OfferField.OFFER_GROUP_ID] || "",
		[OfferField.PRIORITY]:
			typeof apiOffer[OfferField.PRIORITY] === "number" ? apiOffer[OfferField.PRIORITY] : "",
		[OfferField.CHANNEL]: apiOffer[OfferField.CHANNEL] || "",
		[OfferField.START_DATE]: apiOffer[OfferField.START_DATE] || null,
		[OfferField.END_DATE]: apiOffer[OfferField.END_DATE] || null,
		[OfferField.MAX_USAGE_COUNT]:
			typeof apiOffer[OfferField.MAX_USAGE_COUNT] === "number"
				? apiOffer[OfferField.MAX_USAGE_COUNT]
				: "",
		[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]:
			typeof apiOffer[OfferField.INDIVIDUAL_MAX_USAGE_COUNT] === "number"
				? apiOffer[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]
				: "",
		[OfferField.USAGE_COUNT]:
			typeof apiOffer[OfferField.USAGE_COUNT] === "number"
				? apiOffer[OfferField.USAGE_COUNT]
				: 0,
		[OfferField.VERSION]: apiOffer[OfferField.VERSION] || "-",
		[OfferField.STATUS]: status === 0 || status ? String(status) : "",
		createdAt: apiOffer.createdAt || null,
		updatedAt: apiOffer.updatedAt || null,
		[OfferField.RULE]: {
			[OfferRuleField.DESCRIPTION]: rule[OfferRuleField.DESCRIPTION] || "",
			[OfferRuleField.OPERATOR]: rule[OfferRuleField.OPERATOR] || RuleOperator.AND,
			[OfferRuleField.CONDITIONS]: Array.isArray(rule[OfferRuleField.CONDITIONS])
				? rule[OfferRuleField.CONDITIONS]
				: [],
			[OfferRuleField.ACTION]: {
				[OfferActionField.TYPE]: action[OfferActionField.TYPE] || "",
				[OfferActionField.VALUE]:
					typeof action[OfferActionField.VALUE] === "number"
						? action[OfferActionField.VALUE]
						: "",
				[OfferActionField.TARGET]: action[OfferActionField.TARGET] || "",
				[OfferActionField.MIN_AMOUNT_LIMIT]:
					typeof action[OfferActionField.MIN_AMOUNT_LIMIT] === "number"
						? action[OfferActionField.MIN_AMOUNT_LIMIT]
						: "",
				[OfferActionField.MAX_AMOUNT_LIMIT]:
					typeof action[OfferActionField.MAX_AMOUNT_LIMIT] === "number"
						? action[OfferActionField.MAX_AMOUNT_LIMIT]
						: "",
			},
		},
		[OfferField.OFFER_MAPPING]: {
			[OfferMappingField.MENU_IDS]: normalizeMappingValue(
				mapping[OfferMappingField.MENU_IDS]
			),
			[OfferMappingField.RESTAURANT_IDS]: normalizeMappingValue(
				mapping[OfferMappingField.RESTAURANT_IDS]
			),
			[OfferMappingField.USER_IDS]: normalizeMappingValue(
				mapping[OfferMappingField.USER_IDS]
			),
		},
		[OfferField.TERMS_CONDITION]: Array.isArray(apiOffer[OfferField.TERMS_CONDITION])
			? apiOffer[OfferField.TERMS_CONDITION]
			: [],
	};
};